import {RemoteConstantsModel} from "../models/remoteConstants.model";

export interface TidyResult {
    collapsedLevels: number;      // redundant trailing duplicate levels removed
    archivedVestiges: string[];   // entity-id image keys archived (left unused)
}

// Normalize building image packs (legacy cruft cleanup). In-game identical —
// the runtime clamps to the nearest existing level — so this only removes
// redundant/vestigial data:
//   1) Collapse trailing levels identical to the level before them (the old
//      "[[X],[X]]" duplicate seeding: one image stored at two levels, no real
//      progression).
//   2) Blank dead-slot references to the building's own id (the retired
//      "entity image" pinned at a hidden below-startLevel slot), then archive
//      any entity-id image left unreferenced (recoverable from the Archive tab).
// Mutates `data`; returns a summary of what changed. Run on a clone first for a
// dry-run preview.
export function applyImagePackTidy(data: RemoteConstantsModel): TidyResult {
    let collapsedLevels = 0;

    for (const b of data.buildings) {
        const pack = data.imagePacks?.[b.id];
        if (!pack) continue;

        // 1) Collapse trailing duplicate levels.
        while (pack.length > 1 &&
               JSON.stringify(pack[pack.length - 1]) === JSON.stringify(pack[pack.length - 2])) {
            pack.pop();
            collapsedLevels++;
        }

        // 2) Blank dead-slot references to the building's own id.
        const startLevel = Math.max(0, b.startLevel ?? 0);
        const deadBelow = Math.max(0, Math.min(startLevel, pack.length - 1));
        for (let i = 0; i < deadBelow; i++) {
            for (let j = 0; j < pack[i].length; j++) {
                if (pack[i][j] === b.id) pack[i][j] = '';
            }
        }
    }

    // 3) Archive entity-id images now unreferenced anywhere.
    const referenced = new Set<string>();
    for (const pack of Object.values(data.imagePacks ?? {}))
        for (const row of pack) for (const k of row) if (k) referenced.add(k);
    const entityIds = new Set<string>(
        [...data.buildings, ...data.units, ...data.tiles, ...data.resources].map(e => e.id)
    );
    const archivedVestiges: string[] = [];
    for (const key of Object.keys(data.images ?? {})) {
        if (entityIds.has(key) && !referenced.has(key)) {
            data.archive.push({fileName: data.images[key].fileName, key});
            delete data.images[key];
            archivedVestiges.push(key);
        }
    }

    return {collapsedLevels, archivedVestiges};
}
